import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";
import { TYPE_STYLE } from "../constants/opportunities";
import { formatDate, profileInitials } from "../lib/utils";
import { Logo } from "../components/ui/Logo";
import { SearchScreen } from "./SearchScreen";
import { MessagingScreen } from "./MessagingScreen";
import { TrialBanner } from "../components/TrialBanner";
import { OppModal } from "../components/OppModal";
import { EditProfileModal } from "../components/EditProfileModal";
import { ArtworkEditModal } from "../components/ArtworkEditModal";
import { CvSection } from "../components/CvSection";
import { EmptyApplications } from "../components/EmptyCallouts";
import { PricingGuide } from "../components/PricingGuide";
import { GrantWriting } from "../components/GrantWriting";
import { PlanManagement } from "../components/PlanManagement";

const IcoHome = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const IcoWork = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
    <line x1="9" y1="12" x2="15" y2="12"/>
    <line x1="9" y1="16" x2="13" y2="16"/>
  </svg>
);
const IcoSearch = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const IcoMsg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
  </svg>
);
const IcoProfile = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IcoMenu = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const MOBILE_NAV_ITEMS = [
  { id: "home",     Icon: IcoHome    },
  { id: "work",     Icon: IcoWork    },
  { id: "search",   Icon: IcoSearch  },
  { id: "messages", Icon: IcoMsg     },
  { id: "profile",  Icon: IcoProfile },
];

const FUTURA = "'Futura','Century Gothic','Trebuchet MS',sans-serif";
const TYPE_CARD = {
  exhibition: { text:"#c43020", footerBg:"#c43020", footerText:"#f5ede8" },
  grant:      { text:"#0f2d52", footerBg:"#0f2d52", footerText:"#e8e2d9" },
  residency:  { text:"#1e4d32", footerBg:"#1e4d32", footerText:"#dff0e8" },
  open_call:  { text:"#7a5000", footerBg:"#d4940a", footerText:"#1a0e00" },
  opencall:   { text:"#7a5000", footerBg:"#d4940a", footerText:"#1a0e00" },
};

export function ArtistMini({ onPublic, onLogout, bp, profile: profileProp, opportunities }) {
  const [tab, setTab] = useState("opportunities");
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState(false);
  const [typeF, setTypeF] = useState("All");
  const [applications, setApplications] = useState([]);
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(profileProp);
  useEffect(() => {
    if (profileProp) setCurrentProfile(profileProp);
  }, [profileProp]);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [savedIds,  setSavedIds]  = useState(new Set());
  const [savedView, setSavedView] = useState("all");
  const [unreadCount, setUnreadCount] = useState(0);
  const [appsLoaded, setAppsLoaded] = useState(false);
  const [mobileNav, setMobileNav] = useState("home");
  const [workSubTab, setWorkSubTab] = useState("applications");
  const [profileSubView, setProfileSubView] = useState(null);
  const [hamburgerOpen, setHamburgerOpen] = useState(false);

  const navigate = useNavigate();
  const profile = currentProfile;
  const displayName = profile?.display_name || profile?.full_name || 'New Artist';
  const initials = profileInitials(profile);

  // Fetch artist's applications
  useEffect(() => {
    if (!profile) return;
    supabase
      .from('applications')
      .select('*, opportunities(title, org_name, type, deadline)')
      .eq('artist_id', profile.id)
      .order('submitted_at', { ascending: false })
      .then(({ data, error }) => { if (error) console.error('stats:applications', error); if (data) setApplications(data); setAppsLoaded(true); });
  }, [profile]);

  // Fetch portfolio images
  useEffect(() => {
    if (!profile) return;
    supabase
      .from('portfolio_images')
      .select('*')
      .eq('artist_id', profile.id)
      .order('sort_order', { ascending: true })
      .then(({ data }) => { if (data) setPortfolioImages(data); });
  }, [profile]);

  // Unread message count + real-time badge updates
  useEffect(() => {
    if (!profile) return;
    supabase.from('messages').select('id').eq('to_user_id', profile.id).eq('read', false)
      .then(({ data }) => setUnreadCount(data?.length || 0));
    const channel = supabase.channel(`unread-artist-${profile.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, payload => {
        if (payload.new.to_user_id === profile.id) setUnreadCount(n => n + 1);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch saved opportunity IDs
  useEffect(() => {
    if (!profile) return;
    supabase
      .from('saved_opportunities')
      .select('opportunity_id')
      .eq('artist_id', profile.id)
      .then(({ data, error }) => { if (error) console.error('stats:saved', error); if (data) setSavedIds(new Set(data.map(r => r.opportunity_id))); });
  }, [profile]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    setUploading(true);
    setUploadError(null);
    try {
      const ext = file.name.split('.').pop();
      const path = `${profile.id}/${Date.now()}.${ext}`;

      const { error: storageErr } = await supabase.storage
        .from('portfolio-images')
        .upload(path, file);
      if (storageErr) throw storageErr;

      const { data: { publicUrl } } = supabase.storage
        .from('portfolio-images')
        .getPublicUrl(path);

      const title = file.name.replace(/\.[^.]+$/, '');
      const { data: imgData, error: dbErr } = await supabase
        .from('portfolio_images')
        .insert({ artist_id: profile.id, title, image_url: publicUrl, sort_order: portfolioImages.length })
        .select()
        .single();
      if (dbErr) throw dbErr;

      setPortfolioImages(prev => [...prev, imgData]);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const refreshPortfolio = () => {
    if (!profile) return;
    supabase
      .from('portfolio_images')
      .select('*')
      .eq('artist_id', profile.id)
      .order('sort_order', { ascending: true })
      .then(({ data }) => { if (data) setPortfolioImages(data); });
  };

  const handleMobileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    // Reset input so the same file can be re-selected if needed
    e.target.value = '';
    setUploading(true);
    setUploadError(null);
    try {
      const ext = file.name.split('.').pop();
      const path = `${profile.id}/${Date.now()}.${ext}`;
      const { error: storageErr } = await supabase.storage.from('portfolio-images').upload(path, file);
      if (storageErr) throw storageErr;
      const { data: { publicUrl } } = supabase.storage.from('portfolio-images').getPublicUrl(path);
      const title = file.name.replace(/\.[^.]+$/, '');
      const { data: imgData, error: dbErr } = await supabase
        .from('portfolio_images')
        .insert({ artist_id: profile.id, title, image_url: publicUrl, sort_order: portfolioImages.length })
        .select()
        .single();
      if (dbErr) throw dbErr;
      setPortfolioImages(prev => [...prev, imgData]);
      setEditingArtwork(imgData);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const typeFiltered = typeF==="All"?opportunities:opportunities.filter(o=>(
    typeF==="Exhibitions"?o.type==="exhibition":
    typeF==="Grants"?o.type==="grant":
    typeF==="Residency"?o.type==="residency":
    o.type==="residency"
  ));
  const filtered = savedView==="saved"?typeFiltered.filter(o=>savedIds.has(o.id)):typeFiltered;

  const toggleSave = async (oppId) => {
    const wasSaved = savedIds.has(oppId);
    console.log('toggleSave called:', { artist_id: profile?.id, opportunity_id: oppId, wasSaved });
    setSavedIds(prev => { const next = new Set(prev); if (wasSaved) next.delete(oppId); else next.add(oppId); return next; });
    if (wasSaved) {
      const { data, error } = await supabase.from('saved_opportunities').delete().eq('artist_id', profile.id).eq('opportunity_id', oppId);
      console.log('saved_opportunities delete result:', { data, error });
      if (error) setSavedIds(prev => { const next = new Set(prev); next.add(oppId); return next; });
    } else {
      const { data, error } = await supabase.from('saved_opportunities').insert({ artist_id: profile.id, opportunity_id: oppId, saved_at: new Date().toISOString() });
      console.log('saved_opportunities insert result:', { data, error });
      if (error) setSavedIds(prev => { const next = new Set(prev); next.delete(oppId); return next; });
    }
  };

  const handleTabChange = (id) => {
    setTab(id);
    if (id === 'messages' && unreadCount > 0) {
      setUnreadCount(0);
      supabase.from('messages').update({ read: true }).eq('to_user_id', profile.id).eq('read', false);
    }
  };

  const handleMobileNav = (id) => {
    setMobileNav(id);
    setHamburgerOpen(false);
    if (id !== "profile") setProfileSubView(null);
    if (id === "messages" && unreadCount > 0) {
      setUnreadCount(0);
      supabase.from('messages').update({ read: true }).eq('to_user_id', profile.id).eq('read', false);
    }
  };

  const TABS = [
    { id:"opportunities", label:"Opportunities", icon:"📋" },
    { id:"applications",  label:"Applications",  icon:"▤"  },
    { id:"messages",      label:"Messages",       icon:"✉"  },
    { id:"pricing",       label:"Pricing Guide",  icon:"💰" },
    { id:"grants",        label:"Grant Writing",  icon:"✍️" },
    { id:"plan",          label:"Plan",           icon:"💳" },
    { id:"profile",       label:"Profile",        icon:"◎"  },
  ];

  // Desktop search replaces entire UI
  if (search) return <SearchScreen onBack={()=>setSearch(false)} bp={bp} opportunities={opportunities} artists={[]} currentProfile={profile}/>;

  // Unified content key — maps mobile nav state onto the same tab IDs the content blocks use
  const effectiveTab = bp === "mobile"
    ? mobileNav === "home"     ? "opportunities"
    : mobileNav === "messages" ? "messages"
    : mobileNav === "search"   ? "search"
    : mobileNav === "work"     ? workSubTab
    : mobileNav === "profile"  ? (profileSubView ?? "m-profile")
    : "opportunities"
    : tab;

  return (
    <>

      {/* Topnav */}
      <nav className="topnav">
        {bp === "mobile" && mobileNav === "profile" ? (
          <>
            <button onClick={()=>handleMobileNav("home")} style={{background:"none",border:"none",padding:0,cursor:"pointer"}}><Logo/></button>
            <button onClick={()=>setHamburgerOpen(true)} style={{background:"none",border:"none",padding:4,color:"rgba(196,168,130,.7)",lineHeight:0,cursor:"pointer"}}><IcoMenu/></button>
          </>
        ) : (
          <>
            <button
              onClick={() => bp === "mobile" ? handleMobileNav("home") : setTab("opportunities")}
              style={{ background:"none",border:"none",padding:0,cursor:"pointer" }}
            >
              <Logo/>
            </button>
            {bp !== "mobile" && (
              <div style={{ display:"flex",gap:4 }}>
                {TABS.map(t=>(
                  <button key={t.id} className={`nav-lnk ${tab===t.id?"on":""}`} onClick={()=>handleTabChange(t.id)}>
                    <span style={{ position:"relative" }}>
                      {t.label}
                      {t.id==="messages" && unreadCount > 0 && tab !== "messages" && (
                        <span style={{ position:"absolute",top:-8,right:-14,background:C.rose,color:"#fff",borderRadius:"50%",width:16,height:16,fontSize:".46rem",fontFamily:"'DM Mono',monospace",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,lineHeight:1 }}>
                          {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {bp !== "mobile" && (
              <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                <button onClick={()=>setSearch(true)} style={{ background:"rgba(255,255,255,.08)",border:"none",borderRadius:4,padding:"6px 10px",color:C.mutedL,fontSize:".8rem",minHeight:36,display:"flex",alignItems:"center",gap:5 }}>🔍</button>
                <button onClick={onLogout} className="btn-dk" style={{ fontSize:".64rem",padding:"7px 12px",minHeight:36 }}>Log out</button>
              </div>
            )}
          </>
        )}
      </nav>

      <TrialBanner userType="artist" onUpgrade={()=>{}} trialEndsAt={profile?.trial_ends_at} plan={profile?.plan}/>

      {/* Content */}
      <div style={{ maxWidth:1060,margin:"0 auto",padding:bp==="mobile"?"20px 16px 100px":"28px 24px 80px" }}>

        {/* My Work sub-tabs — mobile only, shown when work nav is active */}
        {bp === "mobile" && mobileNav === "work" && (
          <div className="filter-bar" style={{ marginBottom:20 }}>
            {[["applications","Applications"],["pricing","Pricing Guide"],["grants","Grant Writing"]].map(([id,label])=>(
              <button key={id} className="filter-btn"
                style={{ background:workSubTab===id?"#1a1209":"transparent",color:workSubTab===id?"#f0ece2":"rgba(26,18,9,0.45)",border:workSubTab===id?"1.5px solid #1a1209":"1.5px solid rgba(26,18,9,0.12)",fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:"0.12em",textTransform:"uppercase",padding:"8px 18px",borderRadius:3 }}
                onClick={()=>setWorkSubTab(id)}>{label}</button>
            ))}
          </div>
        )}

        {/* Back button when inside a profile sub-view on mobile */}
        {bp === "mobile" && mobileNav === "profile" && profileSubView && (
          <button
            onClick={()=>setProfileSubView(null)}
            style={{ display:"flex",alignItems:"center",gap:6,background:"none",border:"none",padding:"0 0 20px",color:C.muted,fontSize:".76rem",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",cursor:"pointer" }}
          >
            ← Back
          </button>
        )}

        {effectiveTab==="opportunities"&&(
          <>
            <div style={{ marginBottom:22 }}>
              <h1 style={{ fontFamily:FUTURA,fontWeight:900,fontSize:"clamp(1.8rem,4vw,2.4rem)",textTransform:"uppercase",letterSpacing:"-0.02em",color:"#1a1209",marginBottom:6 }}>Open Calls</h1>
              <p style={{ fontSize:14,color:"rgba(26,18,9,0.45)",marginBottom:32 }}>Exhibitions, grants and residencies across Australia.</p>
            </div>
            <div style={{ display:"flex",gap:7,marginBottom:14 }}>
              {[["all","All"],["saved","Saved"]].map(([v,l])=>(
                <button key={v} className="filter-btn"
                  style={{ background:savedView===v?"#1a1209":"transparent",color:savedView===v?"#f0ece2":"rgba(26,18,9,0.45)",border:savedView===v?"1.5px solid #1a1209":"1.5px solid rgba(26,18,9,0.12)",fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:"0.12em",textTransform:"uppercase",padding:"9px 20px",borderRadius:3 }}
                  onClick={()=>setSavedView(v)}>{l}</button>
              ))}
            </div>
            <div className="filter-bar">
              {["All","Exhibitions","Grants","Residency"].map(t=>(
                <button key={t} className="filter-btn"
                  style={{ background:typeF===t?"#1a1209":"transparent",color:typeF===t?"#f0ece2":"rgba(26,18,9,0.45)",border:typeF===t?"1.5px solid #1a1209":"1.5px solid rgba(26,18,9,0.12)",fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:"0.12em",textTransform:"uppercase",padding:"8px 18px",borderRadius:3 }}
                  onClick={()=>setTypeF(t)}>{t}</button>
              ))}
            </div>
            {filtered.length === 0 ? (
              <div className="empty-state" style={{ background:"#ffffff",border:"1px solid rgba(26,18,9,0.08)",borderRadius:4,padding:"72px 32px",textAlign:"center" }}>
                {savedView==="saved" ? (
                  <>
                    <div style={{ width:56,height:56,borderRadius:"50%",background:"rgba(26,18,9,0.05)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px",fontSize:"1.6rem" }}>🔖</div>
                    <div style={{ fontFamily:FUTURA,fontWeight:900,fontSize:18,textTransform:"uppercase",color:"#1a1209",marginBottom:10 }}>No saved opportunities yet</div>
                    <p style={{ fontSize:14,color:"rgba(26,18,9,0.45)",lineHeight:1.7,marginBottom:24 }}>Browse open calls and bookmark ones you're interested in.</p>
                    <button style={{ background:"#c43020",color:"#f5ede8",fontFamily:FUTURA,fontWeight:900,fontSize:11,letterSpacing:"0.1em",textTransform:"uppercase",padding:"13px 28px",borderRadius:3,border:"none",cursor:"pointer" }} onClick={()=>setSavedView("all")}>Browse Open Calls</button>
                  </>
                ) : (
                  <>
                    <div style={{ width:56,height:56,borderRadius:"50%",background:"rgba(26,18,9,0.05)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px",fontSize:"1.6rem" }}>📋</div>
                    <div style={{ fontFamily:FUTURA,fontWeight:900,fontSize:18,textTransform:"uppercase",color:"#1a1209",marginBottom:10 }}>No open calls right now</div>
                    <p style={{ fontSize:14,color:"rgba(26,18,9,0.45)",lineHeight:1.7,marginBottom:24 }}>Check back soon — new opportunities are added regularly.</p>
                  </>
                )}
              </div>
            ) : (
              <div className="opp-grid">
                {filtered.map(o=>{
                  const tc = TYPE_CARD[o.type]||TYPE_CARD.open_call;
                  const tlen = (o.title||"").length;
                  return (
                    <div key={o.id} className="opp-card" onClick={()=>setModal(o)} style={{ background:"#ffffff",border:"1px solid rgba(26,18,9,0.08)",borderRadius:4,overflow:"hidden" }}>
                      <div style={{ padding:"22px 20px 0",display:"flex",justifyContent:"space-between",alignItems:"flex-start" }}>
                        <span style={{ fontFamily:FUTURA,fontWeight:900,fontSize:10,letterSpacing:"0.22em",textTransform:"uppercase",color:tc.text }}>
                          {TYPE_STYLE[o.type]?.label||o.type}
                        </span>
                        <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                          <button
                            onClick={e=>{e.stopPropagation();toggleSave(o.id);}}
                            title={savedIds.has(o.id)?"Remove from saved":"Save opportunity"}
                            style={{ background:"none",border:"none",padding:2,cursor:"pointer",color:savedIds.has(o.id)?"#c43020":"rgba(26,18,9,0.25)",lineHeight:0,transition:"opacity .15s" }}
                            onMouseEnter={e=>e.currentTarget.style.opacity=".7"}
                            onMouseLeave={e=>e.currentTarget.style.opacity="1"}
                          >
                            {savedIds.has(o.id) ? (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
                            )}
                          </button>
                          <span style={{ fontFamily:"'DM Mono',monospace",fontSize:8,border:`1.5px solid ${tc.text}`,borderRadius:2,padding:"5px 10px",color:tc.text,background:"none" }}>
                            {o.entryFee||"No fee"}
                          </span>
                        </div>
                      </div>
                      <div style={{ fontFamily:FUTURA,fontWeight:900,textTransform:"uppercase",lineHeight:0.9,letterSpacing:"-0.02em",color:"#1a1209",padding:"18px 20px 24px",fontSize:tlen<=20?26:tlen<=30?20:16 }}>
                        {o.title}
                      </div>
                      <div style={{ background:tc.footerBg,padding:"20px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:"14px 16px" }}>
                        {[["Who",o.eligibility||"All artists"],["Award",o.prize||o.entryFee||"No prize"],["Where",o.location||o.state],["Closes",o.deadline]].map(([k,v])=>(
                          <div key={k}>
                            <div style={{ fontFamily:"'DM Mono',monospace",fontSize:8,letterSpacing:"0.15em",textTransform:"uppercase",opacity:0.45,color:tc.footerText }}>{k}</div>
                            <div style={{ fontFamily:FUTURA,fontWeight:700,fontSize:12,textTransform:"uppercase",color:tc.footerText }}>{v}</div>
                          </div>
                        ))}
                        <div style={{ gridColumn:"1/-1",borderTop:"1px solid rgba(255,255,255,0.12)",marginTop:2,paddingTop:7,fontFamily:"'DM Mono',monospace",fontSize:7,opacity:0.2,color:tc.footerText }}>opencall.com.au</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {effectiveTab==="applications"&&(
          <div>
            {/* Stats row */}
            {(()=>{
              const pending      = applications.filter(a=>a.status==='pending').length;
              const shortlisted  = applications.filter(a=>a.status==='shortlisted').length;
              const successful   = applications.filter(a=>a.status==='accepted').length;
              const unsuccessful = applications.filter(a=>a.status==='declined').length;
              const tiles = [
                { label:"APPLICATIONS",  value:appsLoaded ? applications.length : "--" },
                { label:"PENDING",       value:appsLoaded ? pending             : "--" },
                { label:"SHORTLISTED",   value:appsLoaded ? shortlisted         : "--" },
                { label:"SUCCESSFUL",    value:appsLoaded ? successful          : "--" },
                { label:"UNSUCCESSFUL",  value:appsLoaded ? unsuccessful        : "--" },
              ];
              return (
                <div style={{ display:"grid",gridTemplateColumns:bp==="mobile"?"repeat(2,1fr)":"repeat(5,1fr)",gap:10,marginBottom:28 }}>
                  {tiles.map(({label,value})=>(
                    <div key={label} style={{ background:"#ffffff",border:"1px solid rgba(26,18,9,0.08)",borderRadius:4,padding:"20px 22px" }}>
                      <div style={{ fontFamily:FUTURA,fontWeight:900,fontSize:"2.2rem",color:"#1a1209",marginBottom:6,lineHeight:1 }}>{value}</div>
                      <div style={{ fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.15em",textTransform:"uppercase",color:"rgba(26,18,9,0.35)" }}>{label}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {applications.length > 0 ? (
              <>
                <h1 style={{ fontFamily:FUTURA,fontWeight:900,fontSize:"clamp(1.8rem,4vw,2.4rem)",textTransform:"uppercase",letterSpacing:"-0.02em",color:"#1a1209",marginBottom:20 }}>My Applications</h1>
                <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
                  {applications.map(app=>{
                    const statusColors = { pending:{bg:C.amberL,c:C.amber}, shortlisted:{bg:"#e8d5f5",c:"#5a2d82"}, accepted:{bg:C.sageL,c:C.sageD}, declined:{bg:C.roseL,c:C.rose} };
                    const sc = statusColors[app.status] || statusColors.pending;
                    return (
                      <div key={app.id} onClick={() => navigate(`/applications/${app.id}`)} style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8,padding:"16px",cursor:"pointer" }}>
                        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12 }}>
                          <div style={{ flex:1,minWidth:0 }}>
                            <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1rem",color:C.bark,marginBottom:3 }}>{app.opportunities?.title || 'Opportunity'}</div>
                            <div style={{ fontSize:".7rem",color:C.muted,fontFamily:"'DM Mono',monospace" }}>{app.opportunities?.org_name} · Deadline {app.opportunities?.deadline ? formatDate(app.opportunities.deadline) : '—'}</div>
                          </div>
                          <div style={{ display:"flex",alignItems:"center",gap:8,flexShrink:0 }}>
                            <span style={{ background:sc.bg,color:sc.c,fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"3px 9px",borderRadius:"2px",fontWeight:600 }}>{app.status}</span>
                            <span style={{ color:C.mutedL,fontSize:".8rem" }}>›</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <EmptyApplications onBrowse={()=>bp==="mobile"?setWorkSubTab("applications"):setTab("opportunities")}/>
            )}
          </div>
        )}

        {effectiveTab==="messages"&&(
          <MessagingScreen profile={profile} bp={bp}/>
        )}

        {effectiveTab==="pricing"&&(
          <PricingGuide bp={bp}/>
        )}

        {effectiveTab==="grants"&&(
          <GrantWriting profile={profile}/>
        )}

        {effectiveTab==="plan"&&(
          <PlanManagement profile={profile} userType="artist"/>
        )}

        {effectiveTab==="profile"&&(
          <div>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22 }}>
              <h1 style={{ fontFamily:FUTURA,fontWeight:900,fontSize:"clamp(1.8rem,4vw,2.4rem)",textTransform:"uppercase",letterSpacing:"-0.02em",color:"#1a1209" }}>My Profile</h1>
              <button style={{ background:"transparent",border:"1.5px solid rgba(26,18,9,0.15)",color:"rgba(26,18,9,0.5)",fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.1em",textTransform:"uppercase",padding:"8px 16px",borderRadius:3,cursor:"pointer" }} onClick={()=>setShowEditProfile(true)}>Edit Profile</button>
            </div>
            {/* Profile hero */}
            <div style={{ background:"#1a1209",borderRadius:4,padding:"36px",marginBottom:22,position:"relative",overflow:"hidden" }}>
              <div style={{ position:"absolute",bottom:-12,right:-4,fontFamily:"'DM Mono',monospace",fontSize:"3.5rem",color:"rgba(240,236,226,0.04)",whiteSpace:"nowrap",letterSpacing:"-2px",pointerEvents:"none" }}>opencall.</div>
              <div style={{ display:"flex",gap:16,alignItems:"flex-start",position:"relative",zIndex:1 }}>
                {profile?.avatar_url
                  ? <img src={profile.avatar_url} alt="" style={{ width:60,height:60,borderRadius:"50%",objectFit:"cover",flexShrink:0,border:"3px solid rgba(240,236,226,0.15)" }}/>
                  : <div style={{ width:60,height:60,borderRadius:"50%",background:`linear-gradient(135deg,${C.clay},${C.barkL})`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Mono',monospace",fontSize:".95rem",color:"#fff",flexShrink:0,border:"3px solid rgba(240,236,226,0.15)" }}>{initials}</div>
                }
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontFamily:FUTURA,fontWeight:900,fontSize:"1.8rem",textTransform:"uppercase",letterSpacing:"-0.02em",color:"#f0ece2",marginBottom:6 }}>{displayName}</div>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",color:"rgba(240,236,226,0.4)",marginBottom:6 }}>
                    Artist{profile?.location ? ` · ${profile.location}` : ''}
                  </div>
                  {profile?.mediums?.length > 0 && (
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",color:"rgba(240,236,226,0.4)",marginBottom:6 }}>{profile.mediums.join(' · ')}</div>
                  )}
                  {profile?.bio
                    ? <p style={{ fontSize:14,color:"rgba(240,236,226,0.5)",lineHeight:1.65,margin:0 }}>{profile.bio}</p>
                    : <p style={{ fontSize:14,color:"rgba(240,236,226,0.3)",lineHeight:1.65,margin:0 }}>Complete your profile so galleries can discover your work.</p>
                  }
                  <div style={{ display:"flex",gap:14,marginTop:10,flexWrap:"wrap" }}>
                    {profile?.website && <a href={profile.website} target="_blank" rel="noreferrer" style={{ fontSize:".72rem",color:"rgba(240,236,226,0.6)",fontFamily:"'DM Mono',monospace",textDecoration:"none" }}>🌐 {profile.website.replace(/^https?:\/\//,'')}</a>}
                    {profile?.instagram && <a href={`https://instagram.com/${profile.instagram}`} target="_blank" rel="noreferrer" style={{ fontSize:".72rem",color:"rgba(240,236,226,0.6)",fontFamily:"'DM Mono',monospace",textDecoration:"none" }}>📷 @{profile.instagram}</a>}
                  </div>
                </div>
              </div>
            </div>

            {/* Portfolio */}
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12 }}>
              <span className="slbl" style={{ margin:0,fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.18em",textTransform:"uppercase",color:"rgba(26,18,9,0.4)" }}>Portfolio</span>
              <label style={{ background:"transparent",border:"1.5px solid rgba(26,18,9,0.15)",color:"rgba(26,18,9,0.5)",fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.1em",textTransform:"uppercase",padding:"8px 16px",borderRadius:3,cursor:uploading?"not-allowed":"pointer",opacity:uploading?0.4:1,display:"inline-flex",alignItems:"center" }}>
                {uploading ? "Uploading…" : "+ Add work"}
                <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display:"none" }} onChange={handleUpload} disabled={uploading}/>
              </label>
            </div>
            {uploadError && (
              <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"10px 14px",marginBottom:12,fontSize:".8rem",color:C.rose,lineHeight:1.5 }}>
                ⚠ Upload failed: {uploadError}<br/>
                <span style={{ fontSize:".72rem",opacity:.8 }}>Make sure the portfolio-images bucket exists in Supabase Storage and is set to Public.</span>
              </div>
            )}
            {portfolioImages.length > 0 ? (
              <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10,marginBottom:28 }}>
                {portfolioImages.map(img=>(
                  <div key={img.id} onClick={()=>setEditingArtwork(img)} style={{ borderRadius:6,overflow:"hidden",border:`1px solid ${C.sand}`,aspectRatio:"1",position:"relative",cursor:"pointer" }}>
                    <img src={img.image_url} alt={img.title} style={{ width:"100%",height:"100%",objectFit:"cover" }}/>
                    <div style={{ position:"absolute",inset:0,background:"rgba(59,47,47,0)",transition:"background .2s" }} className="artwork-hover"/>
                    <div style={{ position:"absolute",bottom:0,left:0,right:0,background:"rgba(59,47,47,.78)",padding:"6px 9px" }}>
                      <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".56rem",color:"#f0e2cc",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{img.title}</div>
                      {(img.medium||img.price) && <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".52rem",color:C.clay,marginTop:1 }}>{[img.medium,img.price?`$${img.price}`:''].filter(Boolean).join(' · ')}</div>}
                    </div>
                    <div style={{ position:"absolute",top:6,right:6,background:"rgba(59,47,47,.7)",borderRadius:4,padding:"3px 7px",fontSize:".6rem",color:"#f0e2cc",fontFamily:"'DM Mono',monospace" }}>Edit</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ background:"#ffffff",border:"1px solid rgba(26,18,9,0.08)",borderRadius:4,padding:"72px 32px",textAlign:"center",marginBottom:28 }}>
                <div style={{ width:56,height:56,borderRadius:"50%",background:"rgba(26,18,9,0.05)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px",fontSize:"1.6rem" }}>🖼️</div>
                <div style={{ fontFamily:FUTURA,fontWeight:900,fontSize:18,textTransform:"uppercase",color:"#1a1209",marginBottom:10 }}>No portfolio images yet</div>
                <p style={{ fontSize:14,color:"rgba(26,18,9,0.45)",lineHeight:1.7,marginBottom:24 }}>Add your work so galleries can discover your practice.</p>
                <label style={{ background:"#c43020",color:"#f5ede8",fontFamily:FUTURA,fontWeight:900,fontSize:11,letterSpacing:"0.1em",textTransform:"uppercase",padding:"13px 28px",borderRadius:3,border:"none",cursor:uploading?"not-allowed":"pointer",opacity:uploading?0.4:1,display:"inline-flex",alignItems:"center" }}>
                  {uploading ? "Uploading…" : "Upload first image"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display:"none" }} onChange={handleUpload} disabled={uploading}/>
                </label>
              </div>
            )}

            {/* CV */}
            <CvSection
              profile={profile}
              onCvSaved={(cv)=>setCurrentProfile(p=>({...p,cv}))}
            />
          </div>
        )}

        {effectiveTab==="search"&&(
          <SearchScreen onBack={()=>setMobileNav("home")} bp={bp} opportunities={opportunities} artists={[]} currentProfile={profile}/>
        )}

        {/* Mobile profile screen — Instagram style */}
        {effectiveTab==="m-profile"&&(
          <div>
            {/* Profile header */}
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",paddingBottom:24,borderBottom:`1px solid ${C.sand}`,marginBottom:20}}>
              {profile?.avatar_url
                ? <img src={profile.avatar_url} alt="" style={{width:88,height:88,borderRadius:"50%",objectFit:"cover",border:`3px solid ${C.sand}`,marginBottom:14}}/>
                : <div style={{width:88,height:88,borderRadius:"50%",background:`linear-gradient(135deg,${C.clay},${C.barkL})`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Mono',monospace",fontSize:"1.4rem",color:"#fff",marginBottom:14,border:`3px solid ${C.sand}`}}>{initials}</div>
              }
              <div style={{fontFamily:FUTURA,fontWeight:900,fontSize:"1.4rem",textTransform:"uppercase",letterSpacing:"-0.02em",color:"#1a1209",marginBottom:5,textAlign:"center"}}>{displayName}</div>
              {(profile?.location||profile?.mediums?.length>0)&&(
                <div style={{fontSize:".76rem",color:"rgba(26,18,9,0.45)",marginBottom:profile?.bio?10:14,textAlign:"center",fontFamily:"'DM Mono',monospace",letterSpacing:"0.05em"}}>
                  {[profile?.location,...(profile?.mediums||[])].filter(Boolean).join(' · ')}
                </div>
              )}
              {profile?.bio&&(
                <p style={{fontSize:14,color:"rgba(26,18,9,0.45)",lineHeight:1.65,margin:"0 0 14px",alignSelf:"flex-start"}}>{profile.bio}</p>
              )}
              <button style={{background:"transparent",border:"1.5px solid rgba(26,18,9,0.15)",color:"rgba(26,18,9,0.5)",fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.1em",textTransform:"uppercase",padding:"8px 16px",borderRadius:3,cursor:"pointer",alignSelf:"flex-start"}} onClick={()=>setShowEditProfile(true)}>Edit Profile</button>
            </div>
            {/* Portfolio section header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <span className="slbl" style={{margin:0,fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.18em",textTransform:"uppercase",color:"rgba(26,18,9,0.4)"}}>Portfolio</span>
              <label style={{background:"transparent",border:"1.5px solid rgba(26,18,9,0.15)",color:"rgba(26,18,9,0.5)",fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.1em",textTransform:"uppercase",padding:"8px 16px",borderRadius:3,cursor:uploading?"not-allowed":"pointer",opacity:uploading?0.4:1,display:"inline-flex",alignItems:"center"}}>
                {uploading?"Uploading…":"+ Add Work"}
                <input type="file" accept="image/jpeg,image/png,image/webp" style={{display:"none"}} onChange={handleMobileUpload} disabled={uploading}/>
              </label>
            </div>
            {uploadError&&(
              <div style={{background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"9px 12px",marginBottom:10,fontSize:".76rem",color:C.rose}}>
                ⚠ Upload failed: {uploadError}
              </div>
            )}
            {/* Portfolio grid */}
            {portfolioImages.length>0?(
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:3,margin:"0 -16px"}}>
                {portfolioImages.map(img=>(
                  <div key={img.id} onClick={()=>setEditingArtwork(img)} style={{position:"relative",paddingBottom:"100%",overflow:"hidden",cursor:"pointer"}}>
                    <img src={img.image_url} alt={img.title} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}/>
                  </div>
                ))}
              </div>
            ):(
              <div style={{background:"#ffffff",border:"1px solid rgba(26,18,9,0.08)",borderRadius:4,padding:"72px 32px",textAlign:"center"}}>
                <div style={{width:56,height:56,borderRadius:"50%",background:"rgba(26,18,9,0.05)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px",fontSize:"1.6rem"}}>🖼️</div>
                <div style={{fontFamily:FUTURA,fontWeight:900,fontSize:18,textTransform:"uppercase",color:"#1a1209",marginBottom:10}}>No portfolio yet</div>
                <p style={{fontSize:14,color:"rgba(26,18,9,0.45)",lineHeight:1.7,marginBottom:24}}>Add your work so galleries can discover your practice.</p>
                <label style={{background:"#c43020",color:"#f5ede8",fontFamily:FUTURA,fontWeight:900,fontSize:11,letterSpacing:"0.1em",textTransform:"uppercase",padding:"13px 28px",borderRadius:3,border:"none",cursor:uploading?"not-allowed":"pointer",opacity:uploading?0.4:1,display:"inline-flex",alignItems:"center"}}>
                  {uploading?"Uploading…":"+ Add your first work"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" style={{display:"none"}} onChange={handleMobileUpload} disabled={uploading}/>
                </label>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Bottom nav */}
      {bp !== "desktop" && (
        <div className="bottom-tabs">
          {bp === "mobile" ? (
            MOBILE_NAV_ITEMS.map(({ id, Icon }) => (
              <button
                key={id}
                className={`tab-btn ${mobileNav === id ? "on" : ""}`}
                onClick={() => handleMobileNav(id)}
              >
                {id === "messages" && unreadCount > 0 && mobileNav !== "messages" && (
                  <span className="tab-badge" style={{ background:C.rose, right:"calc(50% - 16px)" }}>
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
                <Icon/>
              </button>
            ))
          ) : (
            TABS.map(t => (
              <button key={t.id} className={`tab-btn ${tab===t.id?"on":""}`} onClick={()=>handleTabChange(t.id)}>
                {t.id==="messages" && unreadCount > 0 && tab !== "messages" && (
                  <span className="tab-badge" style={{ background:C.rose }}>{unreadCount > 9 ? "9+" : unreadCount}</span>
                )}
                <span className="tab-icon">{t.icon}</span>{t.label}
              </button>
            ))
          )}
        </div>
      )}

      {modal&&<OppModal item={modal} onClose={()=>setModal(null)} currentProfile={profile} isSaved={savedIds.has(modal.id)} onToggleSave={()=>toggleSave(modal.id)} onApply={()=>{
        supabase
          .from('applications')
          .select('*, opportunities(title, org_name, type, deadline)')
          .eq('artist_id', profile.id)
          .order('submitted_at', { ascending: false })
          .then(({ data }) => { if (data) setApplications(data); });
      }}/>}

      {/* Hamburger drawer — mobile profile only */}
      {hamburgerOpen&&(
        <>
          <div onClick={()=>setHamburgerOpen(false)} style={{position:"fixed",inset:0,background:"rgba(20,13,10,.55)",zIndex:400}}/>
          <div style={{position:"fixed",top:0,right:0,bottom:0,width:"72vw",maxWidth:280,background:C.bark,zIndex:401,display:"flex",flexDirection:"column",boxShadow:"-8px 0 32px rgba(0,0,0,.3)"}}>
            <div style={{display:"flex",justifyContent:"flex-end",padding:"18px 20px",borderBottom:"1px solid rgba(255,255,255,.08)"}}>
              <button onClick={()=>setHamburgerOpen(false)} style={{background:"none",border:"none",color:"rgba(196,168,130,.6)",fontSize:"1.3rem",lineHeight:1,cursor:"pointer",padding:4}}>✕</button>
            </div>
            <div style={{flex:1}}>
              <button onClick={()=>{setHamburgerOpen(false);setShowEditProfile(true);}} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"17px 24px",background:"none",border:"none",borderBottom:"1px solid rgba(255,255,255,.06)",color:"#e8d5b7",fontSize:".88rem",textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                <span>Edit Profile</span>
                <span style={{color:"rgba(196,168,130,.4)",fontSize:"1.1rem"}}>›</span>
              </button>
              <button onClick={()=>{setHamburgerOpen(false);setProfileSubView("plan");}} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"17px 24px",background:"none",border:"none",borderBottom:"1px solid rgba(255,255,255,.06)",color:"#e8d5b7",fontSize:".88rem",textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                <span>Plan & Billing</span>
                <span style={{color:"rgba(196,168,130,.4)",fontSize:"1.1rem"}}>›</span>
              </button>
              <div style={{borderTop:"1px solid rgba(255,255,255,.06)",margin:"8px 0"}}/>
              <button onClick={onLogout} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"17px 24px",background:"none",border:"none",color:C.rose,fontSize:".88rem",textAlign:"left",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                <span>Log Out</span>
                <span style={{color:"rgba(196,168,130,.4)",fontSize:"1.1rem"}}>›</span>
              </button>
            </div>
          </div>
        </>
      )}

      {showEditProfile&&(
        <EditProfileModal
          profile={profile}
          onClose={()=>setShowEditProfile(false)}
          onSaved={(updated)=>setCurrentProfile(updated)}
        />
      )}

      {editingArtwork&&(
        <ArtworkEditModal
          image={editingArtwork}
          onDeleted={()=>{
            refreshPortfolio();
            setEditingArtwork(null);
          }}
          onClose={()=>setEditingArtwork(null)}
          onSaved={()=>{
            refreshPortfolio();
            setEditingArtwork(null);
          }}
        />
      )}
    </>
  );
}
